import { useEffect, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import api from "../api/api";
import toast from "react-hot-toast";

const MandatoryRedirector = () => {
  const userId = localStorage.getItem("userId");
  const username = localStorage.getItem("username");
  const token = localStorage.getItem("token");
  const history = useHistory();
  const location = useLocation();

  // ✅ Persist mandatory survey ID
  const currentMandatoryId = useRef(null);

  // ✅ Track SSE status
  const isSseConnected = useRef(false);

  // ✅ Prevent actions after unmount
  const isMounted = useRef(true);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // ✅ Central redirect handler (FIXES ALL RACE ISSUES)
  const redirectToMandatory = (surveyId, source = "") => {
    if (!isMounted.current) return;

    if (
      currentMandatoryId.current === surveyId ||
      location.pathname.startsWith(`/take/${surveyId}`)
    ) {
      return;
    }

    console.log(`🚀 Redirecting [${source}] →`, surveyId);

    currentMandatoryId.current = surveyId;
    toast.dismiss();
    toast.success("Mandatory quiz/survey required.");

    history.push(`/take/${surveyId}`);
  };

  // ─────────────────────────────────────────────
  // 🔒 Block back-navigation
  // ─────────────────────────────────────────────
  useEffect(() => {
    const preventBackFromMandatory = async () => {
      if (!userId) return;

      try {
        const res = await api.fetchMandatorySurvey(userId);
        const dataList = res.data?.data?.content;

        const mandatory = dataList?.find(
          (item) => item.isMandatory && !item.isParticipated,
        );
        if (
          mandatory &&
          !location.pathname.startsWith(`/take/${mandatory.id}`)
        ) {
          currentMandatoryId.current = mandatory.id;
          console.log("🔒 Blocking back navigation");
          history.replace(`/take/${mandatory.id}`);
        }
      } catch (err) {
        console.error("❌ Back navigation check failed:", err);
      }
    };

    preventBackFromMandatory();
  }, [location, userId, history]);

  // ─────────────────────────────────────────────
  // 🚀 Initial REST check (with slight delay)
  // ─────────────────────────────────────────────
  useEffect(() => {
    if (!userId || location.pathname.startsWith("/take/")) return;

    const timeout = setTimeout(() => {
      api
        .fetchMandatorySurvey(userId)
        .then((res) => {
          const dataList = res.data?.data?.content;

          const mandatory = dataList?.find(
            (item) => item.isMandatory && !item.isParticipated,
          );

          if (!mandatory) return;

          redirectToMandatory(mandatory.id, "REST");
        })
        .catch((err) => {
          console.error("❌ Mandatory check failed:", err);
        });
    }, 300); // 🧠 prevents race

    return () => clearTimeout(timeout);
  }, [location.pathname, userId]);

  // ─────────────────────────────────────────────
  // 📡 SSE Listener
  // ─────────────────────────────────────────────
  useEffect(() => {
    if (!userId || !username || !token) return;

    const handleMandatoryPush = (data) => {
      const surveyId = data.quizSurveyId ?? data.id;

      if (!data.isMandatory || !surveyId) return;

      redirectToMandatory(surveyId, "SSE");
    };

    api.connectSse({
      username,
      token,
      onOpen: () => {
        console.log("🟢 SSE connected");
        isSseConnected.current = true;
      },
      onError: () => {
        console.log("🔴 SSE error → fallback polling");
        isSseConnected.current = false;
      },
      onAuthError: () => {
        console.warn("🔒 SSE auth error");
        isSseConnected.current = false;
      },
      onMandatoryPush: handleMandatoryPush,
    });

    return () => {
      api.disconnectSse();
      isSseConnected.current = false;
    };
  }, [userId, username, token]);

  // ─────────────────────────────────────────────
  // ⏱️ Polling fallback (ONLY when SSE fails)
  // ─────────────────────────────────────────────
  useEffect(() => {
    if (!userId) return;

    const interval = setInterval(() => {
      if (isSseConnected.current) return;

      console.log("⏱️ Polling fallback...");

      api
        .fetchMandatorySurvey(userId)
        .then((res) => {
          const dataList = res.data?.data?.content;

          const mandatory = dataList?.find(
            (item) => item.isMandatory && !item.isParticipated,
          );

          if (!mandatory) return;

          redirectToMandatory(mandatory.id, "POLLING");
        })
        .catch(() => {});
    }, 20000);

    return () => clearInterval(interval);
  }, [userId]);

  return null;
};

export default MandatoryRedirector;
